import React from 'react';
import {
    Badge, Button, ListGroup, ListGroupItem, Progress, Row, Col,
    DropdownMenu, DropdownItem, DropdownToggle, UncontrolledButtonDropdown,
    Nav, NavItem, NavLink, TabContent, TabPane,
    UncontrolledTooltip,
} from 'reactstrap';
import moment from 'moment';
import cls from 'classnames';
import {
    faCalendarDay, faTimes, faPlus, faTruck, faUser, faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { httpClient } from '@s20/services/httpClient';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    TextFilterDefaults,
    LinkCellRenderer,
    ButtonCellRenderer,
    DateFilterDefaults,
    DateRangeReadOnlyFloatingFilterComponent,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import Chip from '../common/forms/Chip';
import CommonContext, {
    ApiRoutes,
    ApplicationPermissions,
    AppNavPaths,
} from '../Common';
import {
    FlexBetweenRow,
    FlexCenterRow,
    FlexColumnStart,
    FlexStartRow,
    GroupedRow,
    PageHeading,
    PageWrap,
    SubHeading,
} from '../common/forms/FormElements';
import { Can } from '../Can';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridMultiSelectFilter from '../common/dataGrid/DataGridMultiSelectFilter';
import DataGridMultiSelectFloatingFilter from '../common/dataGrid/DataGridMultiSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import Slideout from '../common/Slideout';
import authService from '../api-authorization/AuthorizeService';
import { LongTermStatusType } from '../longTermStatus/LongTermStatus';
import { EquipmentLongTermStatusForm } from '../equipment/EquipmentLongTermStatusForm';
import { EquipmentLongTermStatusInfo } from '../equipment/EquipmentLongTermStatusInfo';
import { EmployeeLongTermStatusForm } from './EmployeeLongTermStatusForm';
import './DailyRosterView.scss';

class DailyRosterView extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.employeeLongTermStatusFormRef = React.createRef();
        this.equipmentLongTermStatusFormRef = React.createRef();
        this.state = {
            loading: true,
            loadingData: false,
            rowData: [],
            selectedRow: null,
            showEmployeeDetails: false,
            selectedEmployee: null,
            assignedOnly: false,
            cancellationsOnly: false,
            dailyAttendanceEvents: [],
            longTermStatuses: [],
            showLongTermStatusCountOnRoster: false,
            jobAssignmentCancellationTypes: [],
            showEmployeeLongTermStatusForm: false,
            activeTab: '1',
            loadingEquipmentData: false,
            equipmentRowData: [],
            showEquipmentDetails: false,
            selectedEquipment: null,
            equipmentLongTermStatuses: [],
            showEquipmentLongTermStatusForm: false,
            selectedEquipmentLongTermStatus: null,
            selectedEquipmentLongTermStatuses: [],
        };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    onCloseView = () => this.setState({ selectedEmployee: null, showEmployeeDetails: false });

    getDailyAttendanceCount = (dailyAttendanceEventId) => {
        const { scoreboardData } = this.state;

        if (
            !scoreboardData
            || !scoreboardData.dailyAttendanceEventCounts
            || !scoreboardData.dailyAttendanceEventCounts.length
        ) {
            return 0.0;
        }

        const dailyAttendanceEventCount = scoreboardData.dailyAttendanceEventCounts.find(
            (dae) => dae.dailyAttendanceEventId == dailyAttendanceEventId,
        );

        return dailyAttendanceEventCount
            ? dailyAttendanceEventCount.count
            : 0.0;
    };

    getDisplayJobAssignmentCancellationTypes = () => {
        const { jobAssignmentCancellationTypes } = this.state;

        if (!jobAssignmentCancellationTypes) {
            return [];
        }

        return jobAssignmentCancellationTypes.filter(
            (jact) => jact.includeOnRosterView,
        );
    };

    getJobAssignmentCancellationTypeCount = (
        jobAssignmentCancellationTypeId,
    ) => {
        const { scoreboardData } = this.state;

        if (
            !scoreboardData
            || !scoreboardData.jobAssignmentCancellationTypeCounts
            || !scoreboardData.jobAssignmentCancellationTypeCounts.length
        ) {
            return 0.0;
        }

        const jobAssignmentCancellationTypeCount = scoreboardData.jobAssignmentCancellationTypeCounts.find(
            (jact) => jact.jobAssignmentCancellationTypeId
                == jobAssignmentCancellationTypeId,
        );

        return jobAssignmentCancellationTypeCount
            ? jobAssignmentCancellationTypeCount.count
            : 0.0;
    };

    getlongTermStatusCount = (longTermStatusId) => {
        const { scoreboardData } = this.state;

        if (
            !scoreboardData
            || !scoreboardData.longTermStatusCounts
            || !scoreboardData.longTermStatusCounts.length
        ) {
            return 0.0;
        }

        const longTermStatusCount = scoreboardData.longTermStatusCounts.find(
            (lts) => lts.longTermStatusId == longTermStatusId,
        );

        return longTermStatusCount ? longTermStatusCount.count : 0.0;
    };

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();

        if (isAuthenticated) {
            let [
                dispatchLocations,
                customers,
                dailyAttendanceEvents,
                tenantTenantSettings,
                longTermStatuses,
                allJobAssignmentCancellationTypes,
                equipmentTypes,
                equipmentLongTermStatuses,
            ] = await Promise.all([
                httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
                httpClient.js(ApiRoutes.typeAheads.customers()),
                httpClient.js(ApiRoutes.dailyAttendanceEvent.all()),
                httpClient.js(ApiRoutes.tenant.settings()),
                httpClient.js(ApiRoutes.longTermStatus.byTypeId(LongTermStatusType.Employee)),
                httpClient.js(ApiRoutes.jobAssignmentCancellationType.all()),
                httpClient.js(ApiRoutes.typeAheads.equipmentTypes()),
                httpClient.js(ApiRoutes.longTermStatus.byTypeId(LongTermStatusType.Equipment)),
            ]);

            const jobAssignmentCancellationTypes = allJobAssignmentCancellationTypes.filter(
                (jact) => !jact.treatAsUnassigned,
            );

            const tenantSettings = tenantTenantSettings && tenantTenantSettings.tenantSettings
                ? tenantTenantSettings.tenantSettings
                : {};

            const dailyAttendanceEventsEnabled = Boolean(
                tenantSettings.attendanceEventsEnabled,
            );

            const showLongTermStatusCountOnRoster = Boolean(
                tenantSettings.showLongTermStatusCountOnRoster,
            );

            if (!dailyAttendanceEventsEnabled) {
                dailyAttendanceEvents = [];
            }

            const longTermStatusesEnabled = Boolean(
                tenantSettings.employeeLongTermStatusManagementEnabled,
            );

            if (!longTermStatusesEnabled) {
                longTermStatuses = [];
            }

            const timesheetCheckinsEnabled = Boolean(
                tenantSettings.enableContractTimesheetCheckins,
            );

            const hasJobAccess = this.context.checkUserHasPermission(ApplicationPermissions.job_create)
                || this.context.checkUserHasPermission(ApplicationPermissions.job_edit)
                || this.context.checkUserHasPermission(ApplicationPermissions.job_edit);

            const gridOptions = createGridOptions(this);
            const context = this;

            gridOptions.components = {
                selectFilter: DataGridSelectFilter,
                selectFloatingFilter: DataGridSelectFloatingFilter,
                multiSelectFilter: DataGridMultiSelectFilter,
                multiSelectFloatingFilter: DataGridMultiSelectFloatingFilter,
                linkRenderer: LinkCellRenderer,
                buttonRenderer: ButtonCellRenderer,
            };

            const dispatchFilterParams = {
                suppressFilterButton: true,
                labelText: 'Filter by Dispatch',
                options: dispatchLocations,
                optionsLabel: 'label',
                optionsValue: 'value',
                initialFilterValue: this.context?.user?.dispatchCompanyId,
            };

            const dailyAttendanceEventParams = dailyAttendanceEvents.map(
                (dae) => ({
                    label: dae.name,
                    value: `dailyAttendanceEvent-${dae.id}`,
                }),
            );

            const longTermStatusParams = longTermStatuses.map((lts) => ({
                label: lts.name,
                value: `longTermStatus-${lts.id}`,
            }));

            const jobAssignmentCancellationTypeParams = jobAssignmentCancellationTypes
                .filter((jact) => jact.includeOnRosterView)
                .map((jact) => ({
                    label: jact.description,
                    value: `jobAssignmentCancellationType-${jact.id}`,
                }));

            const statusFilterParams = {
                labelText: 'Filter by Status',
                options: [
                    { label: 'Unassigned', value: 'Unassigned' },
                    { label: 'Assigned', value: 'Assigned' },
                    { label: 'Cancelled', value: 'Cancelled' },
                    { label: 'Unbillable Cancel', value: 'Unbillable Cancel' },
                    ...jobAssignmentCancellationTypeParams,
                    ...longTermStatusParams,
                    ...dailyAttendanceEventParams,
                ],
                optionsLabel: 'label',
                optionsValue: 'value',
            };

            const customerFilterParams = {
                suppressFilterButton: true,
                labelText: 'Filter by Customer',
                options: customers,
                optionsLabel: 'label',
                optionsValue: 'value',
            };

            const eventStatusFilterParams = {
                suppressFilterButton: true,
                labelText: 'Filter by Shift Status',
                options: [
                    { label: 'Shift Start', value: 'Shift Start' },
                    { label: 'Job Start', value: 'Job Start' },
                    { label: 'Job end', value: 'Job end' },
                    { label: 'Shift End', value: 'Shift End' },
                    { label: 'No Show', value: 'No Show' },
                ],
                optionsLabel: 'label',
                optionsValue: 'value',
            };

            gridOptions.columnDefs = [
                {
                    colId: 'DispatchLocation',
                    headerName: 'Dispatching',
                    sortable: true,
                    field: 'employeeDispatchLocation',
                    tooltipField: 'employeeDispatchLocation',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: { ...dispatchFilterParams },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: { ...dispatchFilterParams },
                },
                {
                    colId: 'Employee',
                    headerName: 'Employee',
                    field: 'employee',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'CustomerCompanyId',
                    headerName: 'Customer',
                    sortable: true,
                    field: 'customer',
                    tooltipField: 'customer',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: { ...customerFilterParams },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: { ...customerFilterParams },
                },
                {
                    colId: 'JobNumber',
                    minWidth: 180,
                    flex: 1,
                    headerName: 'Job Number',
                    field: 'jobNumber',
                    sortable: false,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    tooltipValueGetter: ({ data }) => {
                        if (!data) return '';
                        const isCross = data.employeeDispatchLocationId
                            && data.dispatchLocationId
                            && data.employeeDispatchLocationId !== data.dispatchLocationId;
                        return isCross
                            ? `${data.jobNumber || ''} — dispatched to ${data.dispatchLocation}`
                            : data.jobNumber || '';
                    },
                    cellRenderer: (params) => {
                        const { data } = params;
                        if (!data) return null;
                        const isCross = data.employeeDispatchLocationId
                            && data.dispatchLocationId
                            && data.employeeDispatchLocationId !== data.dispatchLocationId;
                        const jobContent = hasJobAccess
                            ? (
                                <LinkCellRenderer
                                    {...params}
                                    href={(id) => `${AppNavPaths.Job}/${id}`}
                                    nameField="jobNumber"
                                    idField="jobId"
                                    title="Go to this Job"
                                />
                            )
                            : data.jobNumber || '';
                        if (!isCross) return jobContent;
                        return (
                            <span className="d-inline-flex align-items-center">
                                {jobContent}
                                <Chip
                                    className="cross-dispatch-pill ml-2"
                                    text={data.dispatchLocation}
                                />
                            </span>
                        );
                    },
                },
                {
                    width: 160,
                    minWidth: 160,
                    colId: 'DateRange',
                    headerName: 'Date',
                    headerClass: 'assignment-date-from',
                    field: 'assignmentDateTime',
                    sortable: true,
                    filter: 'agDateColumnFilter',
                    filterParams: {
                        ...DateFilterDefaults,
                        inRangeInclusive: true,
                        closeOnApply: true,
                        browserDatePicker: true,
                        suppressFilterButton: true,
                        minValidDate: '2021-07-01',
                    },
                    floatingFilter: true,
                    floatingFilterComponent:
                        DateRangeReadOnlyFloatingFilterComponent,
                    floatingFilterComponentParams: {},
                    cellRenderer: (params) => {
                        if (!params.data) return '';

                        return moment(params?.value).isValid
                            ? moment(
                                params.data.assignmentDateTime
                                ?? params.data.date,
                            ).format('M/D/YY')
                            : ' - ';
                    },
                },
                {
                    flex: 0,
                    width: 100,
                    minWidth: 100,
                    colId: 'StartTime',
                    headerName: 'Start',
                    headerClass: 'text-center',
                    field: 'assignmentDateTime',
                    sortable: false,
                    cellRenderer: (params) => {
                        if (params.value === undefined) return '';

                        return params.value
                            ? moment(params.value).format('h:mm A')
                            : ' - ';
                    },
                },
                {
                    flex: 0,
                    width: 120,
                    minWidth: 120,
                    headerName: '# Equipment',
                    headerClass: 'text-center',
                    field: 'numEquipment',
                    sortable: false,
                    cellRenderer: (params) => {
                        if (isNaN(params.value)) return '';
                        return params.value;
                    },
                },
            ];

            if (timesheetCheckinsEnabled == true) {
                gridOptions.columnDefs.push({
                    flex: 0,
                    width: 150,
                    colId: 'LastEvent',
                    headerName: 'Last Event',
                    headerClass: 'text-center',
                    field: 'lastEvent',
                    sortable: true,
                    tooltipField: 'Last Event',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: { ...eventStatusFilterParams },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: { ...eventStatusFilterParams },
                    cellRenderer: (params) => {
                        if (!params.value) return '';
                        const badgeColor = params.value == 'No Show' ? 'danger' : 'secondary';
                        return (
                            <Badge
                                color={badgeColor}
                                style={{ width: '100px' }}
                                className="text-left p-2"
                            >
                                {params.value}
                            </Badge>
                        );
                    },
                });
            }

            gridOptions.columnDefs.push({
                flex: 0,
                width: 150,
                colId: 'Status',
                headerName: 'Status',
                headerClass: 'text-center',
                field: 'status',
                sortable: true,
                tooltipField: 'status',
                filter: 'multiSelectFilter',
                floatingFilter: true,
                filterParams: { ...statusFilterParams },
                floatingFilterComponent: 'multiSelectFloatingFilter',
                floatingFilterComponentParams: { ...statusFilterParams },
                cellRenderer: (params) => {
                    if (!params.value) return '';
                    let badgeColor = 'secondary';
                    switch (params.value) {
                        case 'Assigned':
                            badgeColor = 'success';
                            break;
                        case 'Cancelled':
                            badgeColor = 'warning';
                            break;
                        case 'Call Off':
                        case 'No Show':
                            badgeColor = 'danger';
                            break;
                        default:
                            badgeColor = 'secondary';
                    }
                    return (
                        <Badge
                            color={badgeColor}
                            style={{ width: '100px' }}
                            className="text-left p-2"
                        >
                            {params.value}
                        </Badge>
                    );
                },
            });

            gridOptions.overlayNoRowsTemplate = 'No roster information found for today.';
            // gridOptions.overlayLoadingTemplate = '<h4 class="d-flex flex-row align-items-center gridLoadingOverlay"><i class="fa fa-spin text-danger fa-lg fa-circle-notch mr-2"></i>Loading roster, please wait...</h4>';
            gridOptions.showLoadingOverlay = false;
            gridOptions.setLoading = (b) => {
                this.setState({ loadingData: b });
            };
            gridOptions.onRowClicked = this.rowClicked;
            gridOptions.rowHeight = 32;

            gridOptions.useLoading = true;
            gridOptions.loadingTemplate = '<span className="ag-overlay-loading-center "><i class="fa-spin fa fa-md fa-circle-notch"></i> Loading ...</span>';

            gridOptions.onGridReady = (params) => {
                // default date filter values
                const today = moment()
                    .startOf('day')
                    .format('YYYY-MM-DD HH:mm:ss');
                const dateRangeFilterComponent = params.api.getFilterInstance('DateRange');

                dateRangeFilterComponent.setModel({
                    filterType: 'date',
                    type: 'inRange',
                    dateFrom: today,
                    dateTo: today,
                });

                params.api.onFilterChanged();
                gridOptions.api = params.api;
                params.api.setDatasource(context.state.dataSource, gridOptions);
                context.setState({ gridApi: params.api });
            };

            // API returns an object with counts as well as rowData for the grid.
            // Save counts to the state, and return the rowData to the grid.
            // A page fetch that didn't ask for counts comes back without them;
            // the scoreboard keeps what it already has.
            gridOptions.postProcessData = (data) => {
                context.setState((prevState) => ({
                    rowData: data,
                    showEmployeeDetails: prevState.showEmployeeDetails,
                    selectedEmployee: prevState.selectedEmployee,
                    scoreboardData: data.counts
                        ? { ...data.counts }
                        : prevState.scoreboardData,
                }));

                return { ...data.result };
            };

            const baseDataSource = createDataSource(
                ApiRoutes.employees.roster(),
                gridOptions,
            );

            // The scoreboard counts span the whole filtered result, so paging
            // through it must not re-run them. The grid fetches the first block
            // on every load, filter change and refresh — that fetch asks for the
            // counts; the later blocks a scroll pulls in fetch rows alone and
            // keep the counts already on screen.
            const dataSource = {
                ...baseDataSource,
                getRows(params) {
                    params.includeCounts = params.startRow === 0;
                    return baseDataSource.getRows.call(this, params);
                },
            };

            const equipmentGridOptions = createGridOptions(this);

            equipmentGridOptions.components = {
                selectFilter: DataGridSelectFilter,
                selectFloatingFilter: DataGridSelectFloatingFilter,
                multiSelectFilter: DataGridMultiSelectFilter,
                multiSelectFloatingFilter: DataGridMultiSelectFloatingFilter,
                linkRenderer: LinkCellRenderer,
                buttonRenderer: ButtonCellRenderer,
            };

            const equipmentTypeFilterParams = {
                suppressFilterButton: true,
                labelText: 'Filter by Equipment Type',
                options: equipmentTypes,
                optionsLabel: 'label',
                optionsValue: 'value',
            };

            const equipmentLongTermStatusParams = equipmentLongTermStatuses.map(
                (lts) => ({
                    label: lts.name,
                    value: `longTermStatus-${lts.id}`,
                }),
            );

            const equipmentStatusFilterParams = {
                labelText: 'Filter by Status',
                options: [
                    { label: 'Unassigned', value: 'Unassigned' },
                    { label: 'Assigned', value: 'Assigned' },
                    ...equipmentLongTermStatusParams,
                ],
                optionsLabel: 'label',
                optionsValue: 'value',
            };

            equipmentGridOptions.columnDefs = [
                {
                    colId: 'DispatchLocation',
                    headerName: 'Dispatching',
                    sortable: true,
                    field: 'dispatchLocation',
                    tooltipField: 'dispatchLocation',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: { ...dispatchFilterParams },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: { ...dispatchFilterParams },
                },
                {
                    colId: 'Identifier',
                    headerName: 'Identifier',
                    field: 'identifier',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'EquipmentTypeId',
                    headerName: 'Equipment Type',
                    sortable: true,
                    field: 'equipmentType',
                    tooltipField: 'equipmentType',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: { ...equipmentTypeFilterParams },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: { ...equipmentTypeFilterParams },
                },
                {
                    colId: 'CustomerCompanyId',
                    headerName: 'Customer',
                    sortable: true,
                    field: 'customer',
                    tooltipField: 'customer',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: { ...customerFilterParams },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: { ...customerFilterParams },
                },
                {
                    colId: 'JobNumber',
                    minWidth: 180,
                    flex: 1,
                    headerName: 'Job Number',
                    field: 'jobNumber',
                    sortable: false,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    tooltipValueGetter: ({ data }) => {
                        if (!data) return '';
                        const isCross = data.dispatchLocationId
                            && data.jobDispatchLocationId
                            && data.dispatchLocationId !== data.jobDispatchLocationId;
                        return isCross
                            ? `${data.jobNumber || ''} — dispatched to ${data.jobDispatchLocation}`
                            : data.jobNumber || '';
                    },
                    cellRenderer: (params) => {
                        const { data } = params;
                        if (!data) return null;
                        const isCross = data.dispatchLocationId
                            && data.jobDispatchLocationId
                            && data.dispatchLocationId !== data.jobDispatchLocationId;
                        const jobContent = hasJobAccess
                            ? (
                                <LinkCellRenderer
                                    {...params}
                                    href={(id) => `${AppNavPaths.Job}/${id}`}
                                    nameField="jobNumber"
                                    idField="jobId"
                                    title="Go to this Job"
                                />
                            )
                            : data.jobNumber || '';
                        if (!isCross) return jobContent;
                        return (
                            <span className="d-inline-flex align-items-center">
                                {jobContent}
                                <Chip
                                    className="cross-dispatch-pill ml-2"
                                    text={data.jobDispatchLocation}
                                />
                            </span>
                        );
                    },
                },
                {
                    width: 160,
                    minWidth: 160,
                    colId: 'DateRange',
                    headerName: 'Date',
                    headerClass: 'assignment-date-from',
                    field: 'assignmentDateTime',
                    sortable: true,
                    filter: 'agDateColumnFilter',
                    filterParams: {
                        ...DateFilterDefaults,
                        inRangeInclusive: true,
                        closeOnApply: true,
                        browserDatePicker: true,
                        suppressFilterButton: true,
                    },
                    floatingFilter: true,
                    floatingFilterComponent:
                        DateRangeReadOnlyFloatingFilterComponent,
                    floatingFilterComponentParams: {},
                    cellRenderer: (params) => {
                        if (!params.data) return '';

                        const value = params.data.assignmentDateTime
                            ?? params.data.date;

                        return value
                            ? moment(value).format('M/D/YY')
                            : ' - ';
                    },
                },
                {
                    flex: 0,
                    width: 100,
                    minWidth: 100,
                    colId: 'StartTime',
                    headerName: 'Start',
                    headerClass: 'text-center',
                    field: 'assignmentDateTime',
                    sortable: false,
                    cellRenderer: (params) => {
                        if (params.value === undefined) return '';

                        return params.value
                            ? moment(params.value).format('h:mm A')
                            : ' - ';
                    },
                },
                {
                    flex: 0,
                    width: 150,
                    colId: 'Status',
                    headerName: 'Status',
                    headerClass: 'text-center',
                    field: 'status',
                    sortable: true,
                    tooltipField: 'status',
                    filter: 'multiSelectFilter',
                    floatingFilter: true,
                    filterParams: { ...equipmentStatusFilterParams },
                    floatingFilterComponent: 'multiSelectFloatingFilter',
                    floatingFilterComponentParams: { ...equipmentStatusFilterParams },
                    cellRenderer: (params) => {
                        if (!params.value) return '';
                        let badgeColor = 'secondary';
                        switch (params.value) {
                            case 'Assigned':
                                badgeColor = 'success';
                                break;
                            default:
                                badgeColor = 'secondary';
                        }
                        return (
                            <Badge
                                color={badgeColor}
                                style={{ width: '100px' }}
                                className="text-left p-2"
                            >
                                {params.value}
                            </Badge>
                        );
                    },
                },
            ];

            equipmentGridOptions.overlayNoRowsTemplate = 'No equipment roster information found for today.';
            equipmentGridOptions.showLoadingOverlay = false;
            equipmentGridOptions.setLoading = (b) => {
                this.setState({ loadingEquipmentData: b });
            };
            equipmentGridOptions.onRowClicked = this.equipmentRowClicked;
            equipmentGridOptions.rowHeight = 32;

            equipmentGridOptions.useLoading = true;
            equipmentGridOptions.loadingTemplate = '<span className="ag-overlay-loading-center "><i class="fa-spin fa fa-md fa-circle-notch"></i> Loading ...</span>';

            equipmentGridOptions.onGridReady = (params) => {
                // default date filter values
                const today = moment()
                    .startOf('day')
                    .format('YYYY-MM-DD HH:mm:ss');
                const dateRangeFilterComponent = params.api.getFilterInstance('DateRange');

                dateRangeFilterComponent.setModel({
                    filterType: 'date',
                    type: 'inRange',
                    dateFrom: today,
                    dateTo: today,
                });

                params.api.onFilterChanged();
                equipmentGridOptions.api = params.api;
                params.api.setDatasource(context.state.equipmentDataSource, equipmentGridOptions);
                context.setState({ equipmentGridApi: params.api });
            };

            // API returns an object with counts as well as rowData for the grid.
            // Save counts to the state, and return the rowData to the grid.
            equipmentGridOptions.postProcessData = (data) => {
                context.setState((prevState) => ({
                    equipmentRowData: data,
                    showEquipmentDetails: prevState.showEquipmentDetails,
                    selectedEquipment: prevState.selectedEquipment,
                    equipmentScoreboardData: { ...data.counts },
                }));

                return { ...data.result };
            };

            const equipmentDataSource = createDataSource(
                ApiRoutes.equipment.roster(),
                equipmentGridOptions,
            );

            this.setState({
                loading: false,
                gridOptions,
                dataSource,
                dailyAttendanceEvents,
                longTermStatuses,
                showLongTermStatusCountOnRoster,
                jobAssignmentCancellationTypes,
                equipmentGridOptions,
                equipmentDataSource,
                equipmentLongTermStatuses,
            });
        }
    }

    toggleTab = (tab) => {
        const { activeTab } = this.state;

        if (activeTab !== tab) {
            this.setState({
                activeTab: tab,
                showEmployeeDetails: false,
                selectedEmployee: null,
                showEquipmentDetails: false,
                selectedEquipment: null,
                selectedEquipmentLongTermStatuses: [],
            });
        }
    };

    rowClicked = async (params) => {
        const view = params?.node?.data;
        if (view?.employeeId) {
            await this.getSelectedEmployee(view.employeeId, view.date, view.jobId);
        }
    };

    equipmentRowClicked = async (params) => {
        const view = params?.node?.data;
        if (view?.equipmentId) {
            await this.getSelectedEquipment(view.equipmentId, view.date, view.jobId);
        }
    };

    // jobId scopes the panel to the roster row the user clicked (ROX-4321): only that
    // row's assignment appears. Without a jobId (a row with no job behind it) the panel
    // falls back to every assignment the employee has that day.
    getSelectedEmployee = async (employeeId, date, jobId) => {
        const viewData = await httpClient.post(
            ApiRoutes.employee.rosterView(employeeId),
            { date },
        );
        const assignment_rows = [
            ...viewData.filter(
                (x) => x.type === 'Assignment'
                    && (jobId == null || x.jobId === jobId),
            ),
        ];
        const cancel_rows = [
            ...viewData.filter((x) => x.type === 'Cancellation'),
        ];
        const attendanceEvent_rows = [
            ...viewData.filter((x) => x.type === 'AttendanceEvent'),
        ];
        const lts_rows = [
            ...viewData.filter((x) => x.type === 'LTS'),
        ];

        const assignments = [
            ...new Set(
                assignment_rows.map((x) => ({
                    assignmentDate: x.assignmentDateTime,
                    confirmationStatus: x.confirmationStatus,
                    confirmedOn: x.confirmedOn,
                    customerId: x.customerId,
                    customer: x.customer,
                    dispatchCompanyId: x.dispatchCompanyId,
                    dispatchLocation: x.dispatchLocation,
                    employee: x.employee,
                    employeeDispatchCompanyId: x.employeeDispatchCompanyId,
                    employeeDispatchLocation: x.employeeDispatchLocation,
                    employeeId: x.employeeDispatchLocation,
                    employeeUserId: x.employeeUserId,
                    equipment: x.equipment
                        ? [`${x.equipment} (${x.equipmentType})`]
                        : [],
                    groupName: x.groupName,
                })),
            ),
        ];

        const cancellations = [
            ...new Set(
                cancel_rows.map((x) => ({
                    assignmentDate: x.assignmentDateTime,
                    cancellationReason: x.cancellationReason,
                    cancelledOn: x.cancelledOn,
                    customerId: x.customerId,
                    customer: x.customer,
                    dispatchCompanyId: x.dispatchCompanyId,
                    dispatchLocation: x.dispatchLocation,
                })),
            ),
        ];

        const attendanceEvents = [
            ...new Set(
                attendanceEvent_rows.map((x) => ({
                    assignmentDate: x.assignmentDateTime,
                    attendanceEventId: x.attendanceEventId,
                    attendanceEventName: x.attendanceEventName,
                })),
            ),
        ];

        const employeeLongTermStatuses = [
            ...new Set(
                lts_rows.map((x) => ({
                    employeeLongTermStatusId: x.employeeLongTermStatusId,
                    longTermStatusName: x.longTermStatusName,
                    employeeLTSStartDate: x.employeeLTSStartDate,
                    employeeLTSEndDate: x.employeeLTSEndDate,
                })),
            ),
        ];

        const viewDataProcessed = {
            employee: viewData[0].employee,
            assignments,
            cancellations,
            attendanceEvents,
            employeeId: viewData[0].employeeId,
            date,
            jobId,
            employeeLongTermStatuses,
        };

        this.setState({
            showEmployeeDetails: true,
            selectedEmployee: { ...viewDataProcessed },
        });
    };
    handleRemoveAttendanceEvent = async (attendanceEventId) => {
        const { selectedEmployee, gridApi } = this.state;
        const postData = {
            employeeId: selectedEmployee.employeeId,
            dailyAttendanceEventId: attendanceEventId,
            eventDate: moment(selectedEmployee.date).toDate(),
        };
        await httpClient.post(
            ApiRoutes.employeeDailyAttendanceEvent.delete(),
            postData,
        );

        await this.getSelectedEmployee(
            selectedEmployee.employeeId,
            selectedEmployee.date,
            selectedEmployee.jobId,
        );

        if (gridApi) {
            gridApi.refreshInfiniteCache();
        }
    };
    handleSaveAttendanceEvent = async (id) => {
        const { selectedEmployee, gridApi } = this.state;
        const postData = {
            employeeId: selectedEmployee.employeeId,
            dailyAttendanceEventId: id,
            eventDate: moment(selectedEmployee.date).toDate(),
        };
        await httpClient.post(
            ApiRoutes.employeeDailyAttendanceEvent.upsert(),
            postData,
        );
        await this.getSelectedEmployee(
            selectedEmployee.employeeId,
            selectedEmployee.date,
            selectedEmployee.jobId,
        );
        if (gridApi)
            gridApi.refreshInfiniteCache();
    };

    handleRemoveLongTermStatus = async (id) => {
        const { selectedEmployee, gridApi } = this.state;
        const response = await httpClient
            .delete(
                ApiRoutes.employeeLongTermStatus.delete(
                    id,
                ),
            )
            .catch(this.handleSaveError);

        if (response && !isNaN(response)) {
            await this.getSelectedEmployee(
                selectedEmployee.employeeId,
                selectedEmployee.date,
                selectedEmployee.jobId,
            );

            if (gridApi) {
                gridApi.refreshInfiniteCache();
            }
        }
    };
    toggleEmployeeLongTermStatusForm = (show) => {
        this.setState({ showEmployeeLongTermStatusForm: show });
    };

    onCloseEquipmentView = () => this.setState({
        selectedEquipment: null,
        showEquipmentDetails: false,
        selectedEquipmentLongTermStatuses: [],
    });

    // jobId scopes the panel to the roster row the user clicked (ROX-4321): only that
    // row's assignment appears. Without a jobId (a row with no job behind it) the panel
    // falls back to every assignment the equipment has that day.
    getSelectedEquipment = async (equipmentId, date, jobId) => {
        const [viewData, selectedEquipmentLongTermStatuses] = await Promise.all([
            httpClient.post(
                ApiRoutes.equipment.rosterView(equipmentId),
                { date },
            ),
            httpClient.js(
                ApiRoutes.equipmentLongTermStatus.allForEquipment(equipmentId),
            ),
        ]);

        const assignments = (viewData?.assignments ?? []).filter(
            (asn) => jobId == null || asn.jobId === jobId,
        );

        this.setState({
            showEquipmentDetails: true,
            selectedEquipment: {
                ...viewData, assignments, date, jobId,
            },
            selectedEquipmentLongTermStatuses,
        });
    };

    onAddEquipmentLongTermStatus = () => {
        const { selectedEquipment } = this.state;

        if (!this.equipmentLongTermStatusFormRef.current || !selectedEquipment) return;

        this.equipmentLongTermStatusFormRef.current.open(selectedEquipment.equipmentId);
        this.setState({
            showEquipmentLongTermStatusForm: true,
            selectedEquipmentLongTermStatus: null,
        });
    };

    onEditEquipmentLongTermStatus = (suppliedLongTermStatus) => {
        if (suppliedLongTermStatus.id && this.equipmentLongTermStatusFormRef.current) {
            this.equipmentLongTermStatusFormRef.current.open(
                suppliedLongTermStatus,
            );
            this.setState({
                showEquipmentLongTermStatusForm: true,
                selectedEquipmentLongTermStatus: suppliedLongTermStatus,
            });
        }
    };

    onSaveEquipmentLongTermStatus = async () => {
        const { selectedEquipment, equipmentGridApi } = this.state;

        this.setState({
            showEquipmentLongTermStatusForm: false,
            selectedEquipmentLongTermStatus: null,
        });

        if (!selectedEquipment) return;

        const selectedEquipmentLongTermStatuses = await httpClient.js(
            ApiRoutes.equipmentLongTermStatus.allForEquipment(selectedEquipment.equipmentId),
        );

        this.setState({ selectedEquipmentLongTermStatuses });

        if (equipmentGridApi) {
            equipmentGridApi.refreshInfiniteCache();
        }
    };

    toggleEquipmentLongTermStatusForm = (show) => {
        this.setState({ showEquipmentLongTermStatusForm: show });
    };

    getEquipmentLongTermStatusCount = (longTermStatusId) => {
        const { equipmentScoreboardData } = this.state;

        if (
            !equipmentScoreboardData
            || !equipmentScoreboardData.longTermStatusCounts
            || !equipmentScoreboardData.longTermStatusCounts.length
        ) {
            return 0;
        }

        const longTermStatusCount = equipmentScoreboardData.longTermStatusCounts.find(
            (lts) => lts.longTermStatusId == longTermStatusId,
        );

        return longTermStatusCount ? longTermStatusCount.count : 0;
    };

    render() {
        const {
            rowData,
            gridOptions,
            selectedEmployee,
            scoreboardData,
            dailyAttendanceEvents,
            showLongTermStatusCountOnRoster,
            longTermStatuses,
            loading,
            loadingData,
            saving,
            gridApi,
            dataSource,
            gridStatus,
            showEmployeeDetails,
            activeTab,
            equipmentRowData,
            equipmentGridOptions,
            equipmentDataSource,
            equipmentGridApi,
            equipmentScoreboardData,
            loadingEquipmentData,
            selectedEquipment,
            showEquipmentDetails,
            equipmentLongTermStatuses,
            selectedEquipmentLongTermStatuses,
        } = {
            ...this.state,
        };
        if (loading) {
            return <Progress />;
        }

        const displayJobAssignmentCancellationTypes = this.getDisplayJobAssignmentCancellationTypes();

        const assignmentCount = (selectedEmployee?.assignments ?? []).length;
        const cancelCount = (selectedEmployee?.cancellations ?? []).length;
        const attendanceEventCount = (selectedEmployee?.attendanceEvents ?? []).length;
        const employeeLongTermStatusCount = (selectedEmployee?.employeeLongTermStatuses ?? []).length;
        const equipmentAssignmentCount = (selectedEquipment?.assignments ?? []).length;
        const hasEquipmentAccess = this.context.checkUserHasPermission(
            ApplicationPermissions.equipment_view,
        );

        const crewCountClassAddendum = !dailyAttendanceEvents || !dailyAttendanceEvents.length
            ? 'pb-3'
            : '';

        return (
            <PageWrap id="dailyRosterViewPage">
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faCalendarDay}
                        className="mr-2 text-muted"
                    />
                    <span>Roster View</span>
                </PageHeading>
                <Nav tabs>
                    <NavItem>
                        <NavLink
                            className={cls({
                                active: activeTab === '1',
                            })}
                            onClick={() => {
                                this.toggleTab('1');
                            }}
                        >
                            <>
                                <FontAwesomeIcon
                                    icon={faUsers}
                                    className="mr-2 text-muted"
                                />
                                <span className="mr-2">Employees</span>
                                {!!loadingData && (
                                    <span className="fa fa-spin fa-circle-notch text-danger" />
                                )}
                            </>
                        </NavLink>
                    </NavItem>
                    {hasEquipmentAccess && (
                        <NavItem>
                            <NavLink
                                className={cls({
                                    active: activeTab === '2',
                                })}
                                onClick={() => {
                                    this.toggleTab('2');
                                }}
                            >
                                <>
                                    <FontAwesomeIcon
                                        icon={faTruck}
                                        className="mr-2 text-muted"
                                    />
                                    <span className="mr-2">Equipment</span>
                                    {!!loadingEquipmentData && (
                                        <span className="fa fa-spin fa-circle-notch text-danger" />
                                    )}
                                </>
                            </NavLink>
                        </NavItem>
                    )}
                </Nav>
                <TabContent activeTab={activeTab}>
                    <TabPane tabId="1" />
                </TabContent>
                <TabContent activeTab={activeTab}>
                    <TabPane tabId="2" />
                </TabContent>
                {activeTab === '1' && (
                    <div className="d-flex flex-row flex-nowrap flex-fill">
                        <div className="roster-left">
                            <DataGridToolbar
                                loading={loadingData}
                                hideAdd
                                hideExcelButton
                                hideClearFilterButton
                                gridApi={gridApi}
                                gridOptions={gridOptions}
                                dataSource={dataSource}
                                serverExport={{
                                    apiPath: ApiRoutes.employees.rosterExport(),
                                    filePrefix: 'RosterView',
                                }}
                                serverExportDisabled={
                                    !!saving || !!loading || !!loadingData
                                }
                            />
                            {!!scoreboardData && (
                                <FlexCenterRow
                                    className={cls('pt-2', crewCountClassAddendum)}
                                >
                                    <span
                                        style={{ minWidth: '100px' }}
                                        className="mr-2 text-muted font-weight-bold p-2"
                                    >
                                        Crew Counts:
                                    </span>
                                    <Badge
                                        color="secondary"
                                        style={{ minWidth: '100px' }}
                                        className="mr-3 p-2"
                                    >
                                        Unassigned
                                        <span className="ml-2">
                                            {(
                                                scoreboardData.unassigned / 2
                                            ).toFixed(2)}
                                        </span>
                                    </Badge>
                                    <Badge
                                        color="success"
                                        style={{ minWidth: '100px' }}
                                        className="mr-3 p-2"
                                    >
                                        Assigned
                                        <span className="ml-2">
                                            {(scoreboardData.assigned / 2).toFixed(
                                                2,
                                            )}
                                        </span>
                                    </Badge>
                                    <Badge
                                        color="warning"
                                        style={{ minWidth: '100px' }}
                                        className="mr-3 p-2"
                                    >
                                        Cancelled
                                        <span className="ml-2">
                                            {(scoreboardData.cancelled / 2).toFixed(
                                                2,
                                            )}
                                        </span>
                                    </Badge>
                                    <Badge
                                        color="warning"
                                        style={{ minWidth: '100px' }}
                                        className="mr-3 p-2"
                                    >
                                        Unbillable Cancel
                                        <span className="ml-2">
                                            {(
                                                scoreboardData.unbillableCancel / 2
                                            ).toFixed(2)}
                                        </span>
                                    </Badge>
                                    {displayJobAssignmentCancellationTypes.map(
                                        (jact, i) => {
                                            const jobAssignmentCancellationTypeCount = this.getJobAssignmentCancellationTypeCount(
                                                jact.id,
                                            );

                                            return (
                                                <Badge
                                                    color="danger"
                                                    key={`jact${i}`}
                                                    style={{ minWidth: '100px' }}
                                                    className="mr-3 p-2"
                                                >
                                                    {jact.description}
                                                    <span className="ml-2">
                                                        {jobAssignmentCancellationTypeCount.toFixed(
                                                            2,
                                                        )}
                                                    </span>
                                                </Badge>
                                            );
                                        },
                                    )}
                                </FlexCenterRow>
                            )}
                            {showLongTermStatusCountOnRoster
                                && longTermStatuses
                                && longTermStatuses.length > 0 && (
                                    <FlexCenterRow className="badge-container">
                                        <span
                                            style={{ minWidth: '100px' }}
                                            className="mr-2 text-muted font-weight-bold p-2"
                                        >
                                            Long Term Statuses:
                                        </span>
                                        {longTermStatuses.map((lts) => (
                                            <Badge
                                                color="secondary"
                                                style={{ minWidth: '100px' }}
                                                className="mr-3 p-2"
                                                key={`longTermStatus${lts.id}`}
                                            >
                                                {lts.name}
                                                :
                                                <span className="ml-2">
                                                    {this.getlongTermStatusCount(
                                                        lts.id,
                                                    ).toFixed(2)}
                                                </span>
                                            </Badge>
                                        ))}
                                    </FlexCenterRow>
                                )}
                            {dailyAttendanceEvents
                                && dailyAttendanceEvents.length > 0 && (
                                    <FlexCenterRow className="badge-container pb-3">
                                        <span
                                            style={{ minWidth: '100px' }}
                                            className="mr-2 text-muted font-weight-bold p-2"
                                        >
                                            Daily Attendance Events:
                                        </span>
                                        {dailyAttendanceEvents.map((dae) => (
                                            <Badge
                                                color="secondary"
                                                style={{ minWidth: '100px' }}
                                                className="mr-3 p-2"
                                                key={dae.id}
                                            >
                                                {dae.name}
                                                :
                                                <span className="ml-2">
                                                    {this.getDailyAttendanceCount(
                                                        dae.id,
                                                    ).toFixed(2)}
                                                </span>
                                            </Badge>
                                        ))}
                                    </FlexCenterRow>
                                )}
                            <DataGrid
                                loading={loadingData}
                                domLayout="normal"
                                rowData={rowData}
                                gridOptions={gridOptions}
                                gridStatus={gridStatus}
                            />
                        </div>
                        <Slideout
                            formIcon={faUser}
                            formTitle={selectedEmployee?.employee}
                            show={showEmployeeDetails}
                            onClose={this.onCloseView}
                        >
                            <FlexColumnStart>
                                <FlexStartRow className="mb-1">
                                    <span className="text-success">
                                        <strong>Equipment Assignments</strong>
                                    </span>
                                </FlexStartRow>
                                <FlexColumnStart>
                                    <ListGroup>
                                        {(selectedEmployee?.assignments ?? []).map(
                                            (asn, asnIndex) => (
                                                <ListGroupItem key={asnIndex}>
                                                    <FlexColumnStart>
                                                        <FlexBetweenRow
                                                            className="pl-1 pb-1 flex-nowrap"
                                                            key={asnIndex}
                                                        >
                                                            <FlexStartRow>
                                                                <span className="mr-1">
                                                                    {`${moment(
                                                                        asn.assignmentDate,
                                                                    ).format(
                                                                        'h:mm A',
                                                                    )} /`}
                                                                </span>
                                                                <strong>
                                                                    {
                                                                        asn.customer
                                                                    }
                                                                </strong>
                                                                <Badge
                                                                    color="secondary"
                                                                    className="ml-2"
                                                                >
                                                                    {
                                                                        asn.groupName
                                                                    }
                                                                </Badge>
                                                            </FlexStartRow>
                                                            <span
                                                                className={`assignment-status-label font-weight-bold ${asn.confirmationStatus.toLowerCase()}`}
                                                            >
                                                                {
                                                                    asn.confirmationStatus
                                                                }
                                                            </span>
                                                        </FlexBetweenRow>
                                                        {!!(asn.equipment ?? [])
                                                            .length && (
                                                                <FlexColumnStart className="pl-3">
                                                                    <FlexStartRow className="pb-1">
                                                                        Equipment:
                                                                    </FlexStartRow>
                                                                    <ListGroup className="pl-3">
                                                                        {asn.equipment.map(
                                                                            (
                                                                                e,
                                                                                e_inx,
                                                                            ) => (
                                                                                <div
                                                                                    key={
                                                                                        e_inx
                                                                                    }
                                                                                    className="p-1"
                                                                                >
                                                                                    <span className="fa fa-chevron-right mr-2" />
                                                                                    {
                                                                                        e
                                                                                    }
                                                                                </div>
                                                                            ),
                                                                        )}
                                                                    </ListGroup>
                                                                </FlexColumnStart>
                                                            )}
                                                    </FlexColumnStart>
                                                </ListGroupItem>
                                            ),
                                        )}
                                        {!assignmentCount && (
                                            <ListGroupItem>
                                                <FlexCenterRow className="pl-1 text-center">
                                                    No equipment assignments for
                                                    this date.
                                                </FlexCenterRow>
                                            </ListGroupItem>
                                        )}
                                    </ListGroup>
                                    <FlexStartRow className="mb-1 mt-3">
                                        <span className="text-warning mr-1">
                                            <strong>Cancellations</strong>
                                        </span>
                                        /
                                        <span className="text-danger ml-1 mr-1">
                                            <strong>Call Offs</strong>
                                        </span>
                                        /
                                        <span className="text-danger ml-1">
                                            <strong>No Shows</strong>
                                        </span>
                                    </FlexStartRow>
                                    <FlexColumnStart>
                                        <ListGroup>
                                            {(
                                                selectedEmployee?.cancellations
                                                ?? []
                                            ).map((canc, cancIndex) => (
                                                <ListGroupItem key={cancIndex}>
                                                    <FlexColumnStart className="pb-2">
                                                        <FlexBetweenRow
                                                            className="pb-1 flex-nowrap"
                                                            key={cancIndex}
                                                        >
                                                            <FlexStartRow>
                                                                <span className="mr-1">
                                                                    {`${moment(
                                                                        canc.assignmentDate,
                                                                    ).format(
                                                                        'h:mm A',
                                                                    )} /`}
                                                                </span>
                                                                <strong>
                                                                    {canc.customer}
                                                                </strong>
                                                            </FlexStartRow>
                                                            <Badge color="danger">
                                                                {
                                                                    canc.cancellationReason
                                                                }
                                                            </Badge>
                                                        </FlexBetweenRow>
                                                        <small className="w-100 text-left text-muted">
                                                            {`on ${moment(
                                                                canc.cancelledOn,
                                                            ).format(
                                                                'M/D/YY, h:mm A',
                                                            )}`}
                                                        </small>
                                                    </FlexColumnStart>
                                                </ListGroupItem>
                                            ))}
                                            {!cancelCount && (
                                                <ListGroupItem>
                                                    <FlexCenterRow className="pl-1 text-center">
                                                        No cancellations for this
                                                        date.
                                                    </FlexCenterRow>
                                                </ListGroupItem>
                                            )}
                                        </ListGroup>
                                    </FlexColumnStart>
                                    <FlexStartRow className="mb-1 mt-3">
                                        <span className="text-success">
                                            <strong>Attendance Events</strong>
                                        </span>

                                        <UncontrolledButtonDropdown
                                            size="sm"
                                            className="ml-4"
                                            disabled={attendanceEventCount > 0 || (selectedEmployee?.assignments ?? []).length}
                                            id="daily-attendance-event-button"
                                        >
                                            <DropdownToggle
                                                color="link"
                                                size="sm"
                                                disabled={attendanceEventCount > 0 || (selectedEmployee?.assignments ?? []).length}
                                                onClick={(e) => e.stopPropagation()}

                                            >
                                                <FontAwesomeIcon
                                                    icon={faPlus}
                                                    size="lg"
                                                />
                                            </DropdownToggle>
                                            <DropdownMenu container="body">
                                                {dailyAttendanceEvents.map((dae) => (
                                                    <DropdownItem
                                                        key={dae.id}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            this.handleSaveAttendanceEvent(dae.id);
                                                        }}
                                                    >
                                                        {dae.name}
                                                    </DropdownItem>
                                                ))}
                                            </DropdownMenu>
                                        </UncontrolledButtonDropdown>

                                        {Boolean(attendanceEventCount > 0 || (selectedEmployee?.assignments ?? []).length) && (
                                            <UncontrolledTooltip target="daily-attendance-event-button">
                                                You cannot add an attendance event if the employee already has an attendance event or is assigned to a job.
                                            </UncontrolledTooltip>
                                        )}
                                    </FlexStartRow>

                                    <FlexColumnStart>
                                        <Row>
                                            <Col>
                                                <table className="table table-sm table-bordered">
                                                    <thead className="text-muted">
                                                        <tr>
                                                            <th>Event Name</th>
                                                            <th
                                                                className="text-center"
                                                                style={{ width: '30px' }}
                                                            />
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {!(selectedEmployee?.attendanceEvents ?? []).length ? (
                                                            <tr>
                                                                <td
                                                                    colSpan="2"
                                                                    className="text-center"
                                                                >
                                                                    No attendance events for this date.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            (selectedEmployee?.attendanceEvents ?? []).map((atten) => (
                                                                <tr key={atten.attendanceEventId}>
                                                                    <td>{atten.attendanceEventName}</td>
                                                                    <td className="text-center">
                                                                        <Button
                                                                            type="button"
                                                                            color="link"
                                                                            className="text-danger p-0"
                                                                            size="sm"
                                                                            onClick={() => this.handleRemoveAttendanceEvent(atten.attendanceEventId)}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={faTimes}
                                                                            />
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </Col>
                                        </Row>
                                    </FlexColumnStart>
                                    <FlexStartRow className="mb-1 mt-2">
                                        <span className="text-success">
                                            <strong>Long Term Statuses</strong>
                                        </span>
                                        <Button
                                            size="sm"
                                            color="link"
                                            className="ml-3"
                                            title="Add Long Term Status"
                                            disabled={employeeLongTermStatusCount > 0}
                                            onClick={() => {
                                                this.employeeLongTermStatusFormRef.current.resetForm();
                                                this.employeeLongTermStatusFormRef.current.open(
                                                    selectedEmployee?.employeeId,
                                                );
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faPlus}
                                                size="lg"
                                            />
                                        </Button>
                                    </FlexStartRow>
                                    <FlexColumnStart>
                                        <Row>
                                            <Col>
                                                <table className="table table-sm table-bordered">
                                                    <thead className="text-muted">
                                                        <tr>
                                                            <th>Status Name</th>
                                                            <th>Start Date</th>
                                                            <th>End Date</th>
                                                            <th
                                                                className="text-center"
                                                                style={{ width: '30px' }}
                                                            />
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {!(selectedEmployee?.employeeLongTermStatuses ?? []).length ? (
                                                            <tr>
                                                                <td
                                                                    colSpan="4"
                                                                    className="text-center"
                                                                >
                                                                    No long term status for this date.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            (selectedEmployee?.employeeLongTermStatuses ?? []).map((lts) => (
                                                                <tr key={lts.employeeLongTermStatusId}>
                                                                    <td>{lts.longTermStatusName}</td>
                                                                    <td>{lts.employeeLTSStartDate ? moment(lts.employeeLTSStartDate).format('YYYY-MM-DD') : ''}</td>
                                                                    <td>{lts.employeeLTSEndDate ? moment(lts.employeeLTSEndDate).format('YYYY-MM-DD') : ''}</td>
                                                                    <td className="text-center">
                                                                        <Button
                                                                            type="button"
                                                                            color="link"
                                                                            className="text-danger p-0"
                                                                            size="sm"
                                                                            onClick={() => this.handleRemoveLongTermStatus(lts.employeeLongTermStatusId)}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={faTimes}
                                                                            />
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </Col>
                                        </Row>
                                    </FlexColumnStart>
                                </FlexColumnStart>
                            </FlexColumnStart>
                        </Slideout>
                    </div>
                )}
                {activeTab === '2' && hasEquipmentAccess && (
                    <div className="d-flex flex-row flex-nowrap flex-fill">
                        <div className="roster-left">
                            <DataGridToolbar
                                loading={loadingEquipmentData}
                                hideAdd
                                hideExcelButton
                                hideClearFilterButton
                                gridApi={equipmentGridApi}
                                gridOptions={equipmentGridOptions}
                                dataSource={equipmentDataSource}
                                serverExport={{
                                    apiPath: ApiRoutes.equipment.rosterExport(),
                                    filePrefix: 'EquipmentRosterView',
                                }}
                                serverExportDisabled={
                                    !!saving || !!loading || !!loadingEquipmentData
                                }
                            />
                            {!!equipmentScoreboardData && (
                                <FlexCenterRow className="pt-2 pb-3">
                                    <span
                                        style={{ minWidth: '100px' }}
                                        className="mr-2 text-muted font-weight-bold p-2"
                                    >
                                        Equipment Counts:
                                    </span>
                                    <span
                                        style={{ minWidth: '100px' }}
                                        className="mr-3 badge badge-lg badge-secondary p-2"
                                    >
                                        Unassigned
                                        <span className="ml-2">
                                            {equipmentScoreboardData.unassigned}
                                        </span>
                                    </span>
                                    <span
                                        style={{ minWidth: '100px' }}
                                        className="mr-3 badge badge-lg badge-success p-2"
                                    >
                                        Assigned
                                        <span className="ml-2">
                                            {equipmentScoreboardData.assigned}
                                        </span>
                                    </span>
                                </FlexCenterRow>
                            )}
                            {showLongTermStatusCountOnRoster
                                && equipmentLongTermStatuses
                                && equipmentLongTermStatuses.length > 0 && (
                                    <FlexCenterRow className="badge-container pb-3">
                                        <span
                                            style={{ minWidth: '100px' }}
                                            className="mr-2 text-muted font-weight-bold p-2"
                                        >
                                            Long Term Statuses:
                                        </span>
                                        {equipmentLongTermStatuses.map((lts) => (
                                            <span
                                                style={{ minWidth: '100px' }}
                                                className="mr-3 badge badge-lg badge-secondary p-2"
                                                key={`equipmentLongTermStatus${lts.id}`}
                                            >
                                                {lts.name}
                                                :
                                                <span className="ml-2">
                                                    {this.getEquipmentLongTermStatusCount(
                                                        lts.id,
                                                    )}
                                                </span>
                                            </span>
                                        ))}
                                    </FlexCenterRow>
                                )}
                            <DataGrid
                                loading={loadingEquipmentData}
                                domLayout="normal"
                                rowData={equipmentRowData}
                                gridOptions={equipmentGridOptions}
                                gridStatus={gridStatus}
                            />
                        </div>
                        <Slideout
                            formIcon={faTruck}
                            formTitle={`${selectedEquipment?.identifier ?? ''}${selectedEquipment?.equipmentType
                                ? ` (${selectedEquipment.equipmentType})`
                                : ''}`}
                            show={showEquipmentDetails}
                            onClose={this.onCloseEquipmentView}
                        >
                            <FlexColumnStart>
                                <FlexStartRow className="mb-1">
                                    <span className="text-success">
                                        <strong>Job Assignments</strong>
                                    </span>
                                </FlexStartRow>
                                <FlexColumnStart>
                                    <ListGroup>
                                        {(selectedEquipment?.assignments ?? []).map(
                                            (asn, asnIndex) => (
                                                <ListGroupItem key={asnIndex}>
                                                    <FlexColumnStart>
                                                        <FlexBetweenRow
                                                            className="pl-1 pb-1 flex-nowrap"
                                                            key={asnIndex}
                                                        >
                                                            <FlexStartRow>
                                                                <span className="mr-1">
                                                                    {`${moment(
                                                                        asn.assignmentDate,
                                                                    ).format(
                                                                        'h:mm A',
                                                                    )} /`}
                                                                </span>
                                                                <strong>
                                                                    {
                                                                        asn.customer
                                                                    }
                                                                </strong>
                                                                <span className="badge badge-secondary ml-2">
                                                                    {
                                                                        asn.jobNumber
                                                                    }
                                                                </span>
                                                            </FlexStartRow>
                                                            <span
                                                                className={`assignment-status-label font-weight-bold ${(asn.confirmationStatus ?? '').toLowerCase()}`}
                                                            >
                                                                {
                                                                    asn.confirmationStatus
                                                                }
                                                            </span>
                                                        </FlexBetweenRow>
                                                        {!!asn.assignedEmployee && (
                                                            <FlexStartRow className="pl-3">
                                                                {`Assigned to: ${asn.assignedEmployee}`}
                                                            </FlexStartRow>
                                                        )}
                                                    </FlexColumnStart>
                                                </ListGroupItem>
                                            ),
                                        )}
                                        {!equipmentAssignmentCount && (
                                            <ListGroupItem>
                                                <FlexCenterRow className="pl-1 text-center">
                                                    No job assignments for
                                                    this date.
                                                </FlexCenterRow>
                                            </ListGroupItem>
                                        )}
                                    </ListGroup>
                                    <Can
                                        I="view"
                                        a="equipmentlongtermstatus"
                                    >
                                        <SubHeading>
                                            Long-Term Statuses
                                        </SubHeading>

                                        <GroupedRow>
                                            <Col sm="12">
                                                <EquipmentLongTermStatusInfo
                                                    onAddEquipmentLongTermStatus={this.onAddEquipmentLongTermStatus}
                                                    equipmentLongTermStatuses={selectedEquipmentLongTermStatuses}
                                                    onEditEquipmentLongTermStatus={this.onEditEquipmentLongTermStatus}
                                                />
                                            </Col>
                                        </GroupedRow>
                                    </Can>
                                </FlexColumnStart>
                            </FlexColumnStart>
                        </Slideout>
                    </div>
                )}
                <div>
                    <EmployeeLongTermStatusForm
                        onClose={() => this.toggleEmployeeLongTermStatusForm(false)}
                        ref={this.employeeLongTermStatusFormRef}
                        show={this.state.showEmployeeLongTermStatusForm}
                        toggleShow={this.toggleEmployeeLongTermStatusForm}
                        onSaveCallback={async () => {
                            this.toggleEmployeeLongTermStatusForm(false);
                            await this.getSelectedEmployee(
                                selectedEmployee.employeeId,
                                selectedEmployee.date,
                                selectedEmployee.jobId,
                            );
                            const { gridApi } = this.state;
                            if (gridApi) gridApi.refreshInfiniteCache();
                        }}
                    />
                </div>
                <Can
                    do="edit"
                    on="equipmentlongtermstatus"
                >
                    <EquipmentLongTermStatusForm
                        onClose={() => this.toggleEquipmentLongTermStatusForm(false)}
                        ref={this.equipmentLongTermStatusFormRef}
                        show={this.state.showEquipmentLongTermStatusForm}
                        toggleShow={this.toggleEquipmentLongTermStatusForm}
                        onSaveCallback={this.onSaveEquipmentLongTermStatus}
                    />
                </Can>
            </PageWrap>
        );
    }
}
export default DailyRosterView;
